import * as React from 'react';

import { usePublicEvents } from '../event-discovery/api';
import { useMyRegisteredEvents } from '../event-orders/api';
import { useMyFollowUps } from '../follow-up/api';
import { useMyMatchSets } from '../matching/api';

import { buildYourEventsView, eventIdsToDescribe, type YourEventsView } from './constants';

/**
 * **What Your Events reads** (`MEM-052`) — and it is nothing new.
 *
 * The page is a composition of the member's own reads that already existed for the screens it
 * replaces: `GET /api/event-orders/mine` (every registration, free and paid, with its status and
 * its event's timezone), `GET /api/matching/mine` (the released sets, which served the cross-event
 * matches screen) and `GET /api/follow-up/mine` (the summaries, which served the Follow up index).
 * Each of those is the member's own and reads the session, never an id (`MEM-052` AC-7). The city
 * and atmosphere each row shows come from the events' public pages, which serve them to anybody.
 *
 * So this slice has no HTTP surface of its own: it calls the three slices' hooks and the discovery
 * slice's, and `buildYourEventsView` decides what the page holds.
 */
/**
 * One column's read, as the page needs it: did it fail, and how to ask again.
 *
 * TW 44145597. Before this the hook returned one `error` for the toast and a view built from
 * `?? []`, so the page could not tell *"nothing to show"* from *"could not look"* — and drew the
 * empty state for both. The columns fail independently, so the answer is per column.
 */
export interface YourEventsRead {
  readonly failed: boolean;
  readonly retry: () => void;
}

export function useYourEvents(): {
  readonly isPending: boolean;
  readonly error: Error | null;
  readonly view: YourEventsView;
  /** *My upcoming events*, and the Next up card above it. */
  readonly registrations: YourEventsRead;
  /** *My follow up*. */
  readonly followUps: YourEventsRead;
} {
  const registrations = useMyRegisteredEvents({ enabled: true });
  const matchSets = useMyMatchSets({ enabled: true });
  const followUps = useMyFollowUps({ enabled: true });

  // One clock reading per render, so the split and the page's reads agree about what "now" is.
  const now = React.useMemo(() => new Date(), []);
  const registrationRows = registrations.data?.registrations;
  const followUpEntries = followUps.data?.followUps.entries;

  const describe = React.useMemo(
    () =>
      eventIdsToDescribe({
        registrations: registrationRows ?? [],
        followUps: followUpEntries ?? [],
        now,
      }),
    [registrationRows, followUpEntries, now],
  );
  const details = usePublicEvents(describe);

  const view = React.useMemo(
    () =>
      buildYourEventsView({
        registrations: registrationRows ?? [],
        matchSets: matchSets.data?.matchSets ?? [],
        followUps: followUpEntries ?? [],
        details,
        now,
      }),
    [registrationRows, matchSets.data, followUpEntries, details, now],
  );

  const refetchRegistrations = registrations.refetch;
  const refetchFollowUps = followUps.refetch;
  const registrationsRead = React.useMemo(
    () => ({
      failed: registrations.isError,
      retry: (): void => {
        void refetchRegistrations();
      },
    }),
    [registrations.isError, refetchRegistrations],
  );
  const followUpsRead = React.useMemo(
    () => ({
      failed: followUps.isError,
      retry: (): void => {
        void refetchFollowUps();
      },
    }),
    [followUps.isError, refetchFollowUps],
  );

  return {
    // The registrations decide whether there is a Next up card at all, so the page waits for
    // them; the follow-ups decide the right-hand column, so it waits for those too. A match set
    // arriving late only changes a line of copy, and does not hold the page.
    isPending: registrations.isPending || followUps.isPending,
    error: registrations.error ?? followUps.error ?? null,
    view,
    /*
      **Which read failed, not merely that one did** (TW 44145597). The `error` above is what the
      toast says; these are what each column draws. Keeping only the first meant the page fell back
      to `?? []` and then rendered "Nothing else on your calendar yet." over a failure — telling a
      member they have no events when the truth is that we could not find out.
    */
    registrations: registrationsRead,
    followUps: followUpsRead,
  };
}
